/**
 * Shared user role + account-status pills.
 *
 *  One source of truth for the two badges that appear wherever a user
 *  is listed — the All Users table, the user detail modal, Login
 *  Activity, Team, and the Tenant detail owner row — so the Role and
 *  Status columns read identically across every panel.
 *
 *  `RolePill` is presentation-only: the caller resolves the label and
 *  picks a tone, because the role taxonomy differs by surface (the
 *  platform admin sees Master Admin / Company Admin / User; a Company
 *  Admin also sees the work roles Manager / Team Lead / Employee). The
 *  shape and colour treatment stay constant — only the words change.
 *
 *  `StatusPill` owns its full logic because the account-status set is
 *  uniform everywhere (Active / Invited / Suspended / Offboarded, plus
 *  the derived Inactive when the parent company is suspended).
 */

/** Tinted tone palette for role pills — light fill, dark ink. Literal
 *  class strings so Tailwind's content scan resolves them. */
// The token-based tones (accent/info/success/danger/muted) already adapt in
// dark mode via their `--color-*-bg` values; only the two literal-hex tones
// (violet/amber) need a `dark:` override so their light fill doesn't glare.
const ROLE_TONES = {
  violet: 'bg-[#ede9fe] text-[#5b21b6] dark:bg-white/[0.06] dark:text-violet-300',
  amber: 'bg-[#FFEDD5] text-[#7c4a03] dark:bg-white/[0.06] dark:text-amber-300',
  accent: 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]',
  info: 'bg-[color:var(--color-info-bg)] text-[color:var(--color-info)]',
  success: 'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)]',
  danger: 'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)]',
  muted: 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)]',
}

const STATUS_LABEL = {
  active: 'Active',
  invited: 'Invited',
  suspended: 'Suspended',
  offboarded: 'Offboarded',
}

export function RolePill({ label, tone = 'muted', className = '' }) {
  if (!label) return <span className="text-[color:var(--color-ink-muted)]">—</span>
  return (
    <span
      className={
        'inline-flex w-fit items-center whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ' +
        (ROLE_TONES[tone] ?? ROLE_TONES.muted) +
        (className ? ' ' + className : '')
      }
    >
      {label}
    </span>
  )
}

export function StatusPill({ status, companyStatus, className = '' }) {
  if (!status) return <span className="text-[color:var(--color-ink-muted)]">—</span>
  // Solid filled status pill with white text. Offboarded is a terminal
  // state, not an alarm, so it reads muted rather than red. Suspended
  // stays red because it's reversible and unusual. Invited gets a warm
  // amber so the "needs action" state stands out.
  //
  // When the parent company is blocked (suspended / offboarded) the user
  // can't access the platform whatever their own status, so the pill mirrors
  // the COMPANY's state — Suspended or Offboarded — a derived display only;
  // the stored user status is untouched and access returns when the company
  // is reactivated.
  // Solid fills in light mode; each tone carries a `dark:` override that
  // softens it to the tinted-pill recipe (neutral tint + coloured text) so
  // the pills don't glare against the dark surface.
  const blockedByCompany = Boolean(companyStatus) && companyStatus !== 'active'
  const effective = blockedByCompany ? companyStatus : status
  const tone =
    effective === 'active'
      ? 'bg-[color:var(--color-success)] dark:bg-white/[0.06] dark:text-emerald-300'
      : effective === 'suspended'
        ? 'bg-[color:var(--color-danger)] dark:bg-white/[0.06] dark:text-rose-300'
        : effective === 'invited'
          ? 'bg-[color:var(--color-warning)] dark:bg-white/[0.06] dark:text-amber-300'
          : 'bg-[color:var(--color-ink-muted)] dark:bg-white/[0.06] dark:text-slate-300'
  const label =
    STATUS_LABEL[effective] ?? effective.charAt(0).toUpperCase() + effective.slice(1)
  return (
    <span
      className={
        'inline-flex w-fit items-center whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold text-white border border-transparent dark:border-white/10 ' +
        tone +
        (className ? ' ' + className : '')
      }
    >
      {label}
    </span>
  )
}
